import { useEffect, useMemo, useRef, useState } from "react";
import { useClosures } from "@/hooks/use-closures";
import { useKitchenCapacityWindows } from "@/hooks/use-kitchen-capacity";
import { useActiveLocationTimezone } from "@/hooks/use-locations";
import { useOpeningHours } from "@/hooks/use-opening-hours";
import { useSpecialEvents } from "@/hooks/use-special-events";
import { isGuestRisk, useAppStore, useTodayReservations } from "@/store/store";
import type { Reservation } from "@/store/types";
import { dayjs, formatCurrentTime, nowInTz, parseTimeHour, todayStr } from "@/utils/date";
import { zoneAccent } from "@/lib/zone-brand-colors";

const barStyles: Record<string, { bg: string; border: string; text: string }> = {
  completada: { bg: "bg-gray-100", border: "border-gray-300", text: "text-gray-500" },
  noshow: { bg: "bg-rose-100", border: "border-rose-400", text: "text-rose-700" },
  reservada: { bg: "bg-blue-100", border: "border-blue-400", text: "text-blue-800" },
  sentado: { bg: "bg-emerald-100", border: "border-emerald-400", text: "text-emerald-800" },
};

/** Display order requested by Natalia: Comedor, Bar, Terraza,
 *  Barra, Patio (anything else lands at the end, in name order).
 *  Match by uppercased zone name so the schema's
 *  ``COMEDOR`` / ``BAR`` / ``BARRA`` / ``TERRAZA`` / ``PATIO``
 *  rows resolve regardless of casing drift across tenants. */
const ZONE_DISPLAY_ORDER = ["COMEDOR", "BAR", "TERRAZA", "BARRA", "PATIO"];

function zoneSortKey(name: string): number {
  const idx = ZONE_DISPLAY_ORDER.indexOf(name.toUpperCase());
  return idx === -1 ? ZONE_DISPLAY_ORDER.length : idx;
}

// Lookup keys are zone names lowercased (Seim\u00B7Seim ships "COMEDOR",
// "BAR", "BARRA", "TERRAZA", "PATIO" \u2014 only ``bar`` and ``terraza``
// resolve to an emoji on that tenant, the others surface without one).
// Earlier this was keyed by ``zone.id`` (a UUID), which never matched.
const zoneEmojis: Record<string, string> = {
  bar: "\uD83C\uDF78",
  barra: "\uD83C\uDF79",
  comedor: "\uD83C\uDF7D",
  jardin: "\uD83C\uDF3F",
  patio: "\uD83C\uDF31",
  salon: "\uD83C\uDF7D",
  "salon-privado": "\uD83C\uDF7D",
  terraza: "\u2600",
};

// Fallback window used while opening hours are loading (or for the
// edge case where the location has no schedule at all, in which case
// the "closed" overlay takes over the render). Keeps the now-line
// math defined for that single frame before the real range lands.
const FALLBACK_HOUR_START = 12;
const FALLBACK_HOUR_END = 23;

/** Quarter-hour tick label. ``4`` → ``"4 PM"``, ``4.25`` → ``"4:15"``,
 *  ``4.5`` → ``"4:30"``, ``4.75`` → ``"4:45"``. The AM/PM suffix only
 *  rides on the on-the-hour ticks so the in-between labels stay short
 *  enough to fit a 15-minute slot. ``24`` is the documented "end of
 *  day" sentinel (close_time = midnight); render as ``"12 AM"``. */
function tickLabel(hour: number): string {
  const totalMin = Math.round(hour * 60);
  const h24 = Math.floor(totalMin / 60) % 24;
  const m = totalMin % 60;
  if (m === 0) {
    if (h24 === 0) return "12 AM";
    if (h24 === 12) return "12 PM";
    return h24 > 12 ? `${h24 - 12} PM` : `${h24} AM`;
  }
  const h12 = h24 % 12 || 12;
  return `${h12}:${m.toString().padStart(2, "0")}`;
}

const TICK_STEP_HOURS = 0.25;

/** Convert a decimal hour into the ``"h:mm A"`` shape the draft and
 *  the rest of the staff app use for reservation times. ``16.5`` →
 *  ``"4:30 PM"``, ``9`` → ``"9:00 AM"``. */
function decimalToTimeString(hour: number): string {
  const totalMin = Math.round(hour * 60);
  const h24 = Math.floor(totalMin / 60) % 24;
  const m = totalMin % 60;
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
}

/** Parse an ``"HH:MM:SS"`` time string into a decimal hour (e.g.
 *  ``"19:30:00"`` → ``19.5``). The backend serialises opening hours
 *  in that exact shape; ``"00:00:00"`` for ``close_time`` is the
 *  documented sentinel for "midnight" (end of the day) and we
 *  surface it as ``24`` so range math doesn't roll back to zero.
 *  Returns ``NaN`` for malformed input so callers can filter via
 *  ``Number.isFinite`` and short-circuit to the "Sin horario" branch
 *  instead of silently collapsing the day's range to midnight. */
function parseHhmmsToHour(value: string, isCloseTime: boolean): number {
  const [hStr, mStr] = value.split(":");
  const h = Number.parseInt(hStr, 10);
  const m = Number.parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return Number.NaN;
  if (isCloseTime && h === 0 && m === 0) return 24;
  return h + m / 60;
}

export default function TimelineGrid() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState(dayjs());

  const zones = useAppStore((s) => s.zones);
  const tables = useAppStore((s) => s.tables);
  const selectedDate = useAppStore((s) => s.selectedDate);
  const setDetailReservationId = useAppStore((s) => s.setDetailReservationId);
  const setShowSidebar = useAppStore((s) => s.setShowSidebar);
  const openNewReservation = useAppStore((s) => s.openNewReservation);
  const closeNewReservation = useAppStore((s) => s.closeNewReservation);
  const todayReservations = useTodayReservations();
  const { data: openingHours } = useOpeningHours();
  const { data: specialEvents } = useSpecialEvents();
  const { data: closures } = useClosures();
  const { data: kitchenWindows } = useKitchenCapacityWindows();
  const locationTz = useActiveLocationTimezone();

  // Track the wall-clock in the LOCATION's timezone, not the browser's.
  // A restaurant in Bogotá viewed from a laptop in CDMX would otherwise
  // see the now-line painted one hour off; the today-check uses the
  // location's clock, so the marker has to match.
  useEffect(() => {
    const refresh = () => setCurrentTime(locationTz ? nowInTz(locationTz) : dayjs());
    refresh();
    const interval = setInterval(refresh, 60_000);
    return () => clearInterval(interval);
  }, [locationTz]);

  // Translate the operator's selected calendar day into the schedule
  // window we should render. The backend stores ``day_of_week`` with
  // ``0 = Monday`` (Django convention); dayjs returns ``0 = Sunday``,
  // so remap before lookup.
  const { hourStart, hourEnd, closedReason } = useMemo(() => {
    const date = dayjs(selectedDate);
    const dayjsDow = date.day(); // 0=Sun..6=Sat
    const backendDow = (dayjsDow + 6) % 7; // 0=Mon..6=Sun

    // Full-day closures (no time bounds) short-circuit to the
    // "Seim·Seim cerrado" panel below. Partial closures land as
    // "Fuera de servicio" strips overlaid on the regular grid — see
    // ``blockingStrips`` further down.
    const fullDayClosure = (closures ?? []).find((c) => {
      if (c.start_time !== null || c.end_time !== null) return false;
      const start = c.date;
      const end = c.end_date ?? c.date;
      return selectedDate >= start && selectedDate <= end;
    });
    if (fullDayClosure) {
      return {
        hourStart: FALLBACK_HOUR_START,
        hourEnd: FALLBACK_HOUR_END,
        closedReason: fullDayClosure.reason || "Cerrado",
      };
    }

    // Still loading the openings query → keep the fallback so the grid lines
    // stay defined for the loading frame. Once it resolves empty (and no event
    // opens the day) we treat it as closed below.
    if (openingHours === undefined) {
      return { hourStart: FALLBACK_HOUR_START, hourEnd: FALLBACK_HOUR_END, closedReason: null };
    }

    // Multiple opening rows for the same day (e.g. almuerzo + cena
    // with a break between them) collapse into one continuous strip
    // from the earliest open to the latest close — the operator
    // still wants to see the in-between hours so they spot late
    // arrivals or operational gaps. NaN values (malformed times)
    // are dropped instead of collapsing the day to midnight.
    const todays = openingHours.filter((oh) => oh.day_of_week === backendDow);
    const opens = todays.map((oh) => parseHhmmsToHour(oh.open_time, false)).filter(Number.isFinite);
    const closes = todays
      .map((oh) => parseHhmmsToHour(oh.close_time, true))
      .filter(Number.isFinite);

    // A special event with custom hours applies on top of the weekday
    // schedule for the day — it can open a normally-closed day or run past
    // the regular close. Fold its window into the ruler (union) so the
    // timeline actually covers the event's hours instead of clipping them.
    const event = (specialEvents ?? []).find((e) => {
      const end = e.end_date ?? e.date;
      return selectedDate >= e.date && selectedDate <= end;
    });
    if (event?.open_time && event.close_time) {
      const eventOpen = parseHhmmsToHour(event.open_time, false);
      const eventClose = parseHhmmsToHour(event.close_time, true);
      if (Number.isFinite(eventOpen)) opens.push(eventOpen);
      if (Number.isFinite(eventClose)) closes.push(eventClose);
    }

    if (opens.length === 0 || closes.length === 0) {
      return {
        hourStart: FALLBACK_HOUR_START,
        hourEnd: FALLBACK_HOUR_END,
        closedReason: "Sin horario",
      };
    }
    const minOpen = Math.min(...opens);
    let maxClose = Math.max(...closes);
    // Overnight wrap: a shift that opens 6 PM and closes at 2 AM
    // arrives with ``close_time = 02:00:00`` (literal, not the midnight
    // sentinel). Without this nudge ``hourEnd`` collapses to 2 and the
    // ruler inverts. Adding 24 keeps the math continuous.
    if (maxClose <= minOpen) maxClose += 24;
    return {
      hourStart: Math.floor(minOpen),
      hourEnd: Math.ceil(maxClose),
      closedReason: null,
    };
  }, [closures, openingHours, specialEvents, selectedDate]);

  const activeReservations = todayReservations.filter((r: Reservation) => r.status !== "cancelada");

  // Build "Fuera de servicio" overlay strips from partial closures
  // (closures with explicit ``start_time`` / ``end_time``) and from
  // any kitchen-capacity window with ``max_pax = 0`` on the
  // selected weekday. Full-day closures already short-circuit to the
  // "Seim·Seim cerrado" view above, so they don't double-render here.
  const blockingStrips = useMemo(() => {
    const date = dayjs(selectedDate);
    const backendDow = (date.day() + 6) % 7;
    const strips: { startHour: number; endHour: number; label: string }[] = [];
    // An overnight window (e.g. 10 PM–2 AM) lands with ``end < start``
    // after parsing; without normalising it the strip's
    // ``visibleEnd <= visibleStart`` check below drops it silently.
    // Add 24 to the end so the strip lives on the post-midnight tail
    // of the same day, and the visible-range clamp does the right
    // thing.
    const pushStrip = (startHour: number, endHour: number, label: string) => {
      if (!Number.isFinite(startHour) || !Number.isFinite(endHour)) return;
      if (endHour < startHour) endHour += 24;
      strips.push({ startHour, endHour, label });
    };
    for (const c of closures ?? []) {
      if (c.start_time === null || c.end_time === null) continue;
      const inRange = selectedDate >= c.date && selectedDate <= (c.end_date ?? c.date);
      if (!inRange) continue;
      pushStrip(
        parseHhmmsToHour(c.start_time, false),
        parseHhmmsToHour(c.end_time, true),
        c.reason || "Fuera de servicio",
      );
    }
    for (const w of kitchenWindows ?? []) {
      if (w.day_of_week !== backendDow) continue;
      if (w.max_pax !== 0) continue;
      pushStrip(
        parseHhmmsToHour(w.start_time, false),
        parseHhmmsToHour(w.end_time, true),
        "Cocina cerrada",
      );
    }
    return strips;
  }, [closures, kitchenWindows, selectedDate]);

  const totalHours = Math.max(1, hourEnd - hourStart);
  // Build the tick array at 15-minute granularity. We render a label
  // (and gridline) at every quarter so the host can place a 15-min
  // arrival quickly without measuring; the in-between labels read
  // shorter ("4:15") so they fit in a slot four times narrower than
  // an hour cell would be.
  const ticks = Array.from(
    { length: Math.max(0, Math.round(totalHours / TICK_STEP_HOURS)) },
    (_, i) => hourStart + i * TICK_STEP_HOURS,
  );

  const nowHour = currentTime.hour() + currentTime.minute() / 60;
  const nowPct = ((nowHour - hourStart) / totalHours) * 100;
  // Only paint the red ``now`` line when the operator is looking at
  // today's grid. On past or future days the line was just a stray
  // red marker stamping wherever ``now`` happened to land — visually
  // suggesting "this is now" on a day that isn't today.
  const isViewingToday = selectedDate === todayStr(locationTz);
  const showNowLine =
    isViewingToday && nowHour >= hourStart && nowHour <= hourEnd && closedReason === null;

  // Snap "now" down to the active quarter so the matching column
  // label can highlight (e.g. nowHour 16.32 → highlight 16.25 = 4:15).
  const currentQuarter = Math.floor(nowHour / TICK_STEP_HOURS) * TICK_STEP_HOURS;

  const tableHasVip = (tableId: string) =>
    activeReservations.some((r: Reservation) => r.tableIds.includes(tableId) && r.isVip);

  // Build the zone display list from the tables we actually have
  // (so empty zones — like the orphan duplicate BARRA in the
  // Seim·Seim tenant — never render), then apply the host's
  // preferred section order (Comedor → Bar → Terraza → Barra →
  // Patio; anything else lands after, alphabetized).
  const uniqueZoneIds = [...new Set(tables.map((t) => t.zoneId))];
  const groupedZones = uniqueZoneIds
    .map((zid) => zones.find((z) => z.id === zid))
    .filter((z): z is NonNullable<typeof z> => Boolean(z))
    .toSorted((a, b) => {
      const order = zoneSortKey(a.name) - zoneSortKey(b.name);
      return order !== 0 ? order : a.name.localeCompare(b.name);
    });

  const totalReservations = activeReservations.length;
  const totalPax = activeReservations.reduce((s: number, r: Reservation) => s + r.pax, 0);
  const tablesWithReservations = new Set(
    activeReservations.flatMap((r: Reservation) => r.tableIds),
  );
  const totalTablesUsed = tablesWithReservations.size;

  // Column width percentages for the timeline area (right of the "Mesa" label column)
  const colLeftPct = (hour: number) => ((hour - hourStart) / totalHours) * 100;

  if (closedReason !== null) {
    // Day with no schedule (no opening hours row + no full-day
    // closure either, or a closure with a reason). Skip the empty
    // grid that read as "free space" and show an explicit closed
    // panel instead — clearer to the host than a blank chart.
    return (
      <div className="flex-1 overflow-hidden flex flex-col px-4 sm:px-8 py-4 sm:py-6">
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2 text-center">
            <span className="text-[12px] font-mono font-bold uppercase tracking-widest text-text-muted">
              {dayjs(selectedDate).format("dddd D [de] MMMM")}
            </span>
            <span className="text-[28px] font-bold text-text-secondary">Seim·Seim cerrado</span>
            <span className="text-[13px] text-text-muted">{closedReason}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    // ``--mesa-col-w`` is the sticky-left "Mesa" column width, shared
    // by the header cell, the zone headers and the row labels, plus
    // the absolute-positioned grid lines and the now-line so they
    // line up with the hour ruler. Narrower on mobile (table names
    // are short — Mesa 1, T6, B1 — so 64 px is plenty), wider once
    // there's room. Adjusting both numbers here re-flows the whole
    // grid consistently.
    <div className="flex-1 overflow-hidden flex flex-col px-4 sm:px-8 py-4 sm:py-6 [--mesa-col-w:64px] sm:[--mesa-col-w:120px]">
      <div ref={scrollRef} className="flex-1 overflow-auto min-w-0">
        <div className="min-w-[1200px]">
          {/* Header row — sticky on the vertical axis so the hour ruler
              stays visible while the host scrolls down through long
              zone lists, and the "Mesa" cell is also sticky on the
              horizontal axis so it stays anchored when the timeline
              extends past the viewport (mobile / narrow tablet).
              Higher z-index than the row labels so it floats above
              everything at the top-left intersection. */}
          <div className="flex items-center h-10 border-b border-blocked-border sticky top-0 bg-surface z-20">
            <div className="sticky left-0 w-[var(--mesa-col-w)] shrink-0 pl-1 text-xs text-text-secondary font-medium bg-surface z-[11]">
              Mesa
            </div>
            <div className="flex-1 flex items-baseline">
              {ticks.map((t) => {
                const isOnTheHour = t % 1 === 0;
                // Only highlight the "current" column on today — on past
                // / future days the highlight was tracking browser
                // wall-clock and contradicting the hidden now-line.
                const isCurrent = isViewingToday && Math.abs(t - currentQuarter) < 1e-9;
                // Quarter labels read a touch smaller than the
                // on-the-hour anchors so the eye still grabs the
                // hour grid first while the :15 / :30 / :45 marks
                // sit quietly underneath.
                const sizeClass = isOnTheHour
                  ? "text-[9px] sm:text-[10px]"
                  : "text-[8px] sm:text-[9px]";
                return (
                  <div
                    key={t}
                    className={`flex-1 truncate ${sizeClass} ${
                      isCurrent
                        ? "text-text-primary font-bold"
                        : isOnTheHour
                          ? "text-text-secondary font-semibold"
                          : "text-text-muted font-medium"
                    }`}
                  >
                    {tickLabel(t)}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Grid body — relative container for the continuous now-line and grid lines */}
          <div className="relative">
            {/* Continuous vertical grid lines — full opacity on the
                hour, dimmer on the 15-min subdivisions so the host's
                eye still snaps to the hour grid first while the
                quarter ticks act as soft guides. */}
            {ticks.map((t) => {
              const isOnTheHour = t % 1 === 0;
              return (
                <div
                  key={`grid-${t}`}
                  className={`absolute top-0 bottom-0 border-l ${
                    isOnTheHour ? "border-border/40" : "border-border/15"
                  }`}
                  style={{
                    left: `calc(var(--mesa-col-w) + (100% - var(--mesa-col-w)) * ${colLeftPct(t) / 100})`,
                  }}
                />
              );
            })}
            {/* Trailing edge of the visible range — the tick array
                covers ``[hourStart, hourEnd)``, so without this last
                line the right side of the grid bleeds into the
                container padding without a closing border. */}
            <div
              className="absolute top-0 bottom-0 border-l border-border/40"
              style={{
                left: `calc(var(--mesa-col-w) + (100% - var(--mesa-col-w)) * ${colLeftPct(hourEnd) / 100})`,
              }}
            />

            {/* "Fuera de servicio" strips — full-height overlay for
                partial closures and kitchen-cap zero windows that fall
                inside the visible range. Striped pattern + label so
                the host reads it as a block rather than free time.
                ``z-[3]`` puts the strip above gridlines but below the
                reservation bars (``z-[5]``), so an existing booking
                inside the strip stays readable. The strip is
                ``pointer-events-none`` so the row's tap-to-create
                still fires for the visible hueco around it. */}
            {blockingStrips.map((strip, idx) => {
              const visibleStart = Math.max(strip.startHour, hourStart);
              const visibleEnd = Math.min(strip.endHour, hourEnd);
              if (visibleEnd <= visibleStart) return null;
              const leftPct = ((visibleStart - hourStart) / totalHours) * 100;
              const widthPct = ((visibleEnd - visibleStart) / totalHours) * 100;
              return (
                <div
                  key={`block-${idx}`}
                  className="absolute top-0 bottom-0 z-[3] pointer-events-none flex items-center justify-center"
                  style={{
                    left: `calc(var(--mesa-col-w) + (100% - var(--mesa-col-w)) * ${leftPct / 100})`,
                    width: `calc((100% - var(--mesa-col-w)) * ${widthPct / 100})`,
                    backgroundImage:
                      "repeating-linear-gradient(45deg, rgba(120,113,108,0.15) 0 6px, rgba(120,113,108,0.08) 6px 12px)",
                    borderLeft: "1px dashed rgba(120,113,108,0.5)",
                    borderRight: "1px dashed rgba(120,113,108,0.5)",
                  }}
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider text-stone-600 bg-white/80 px-1.5 py-0.5 rounded">
                    {strip.label}
                  </span>
                </div>
              );
            })}

            {/* Continuous now-line — full height */}
            {showNowLine && (
              // Now-line sits above reservation bars (``z-[5]``) and
              // strips (``z-[3]``) but BELOW the sticky Mesa column
              // (``z-[11]`` below) so that during horizontal scroll
              // the red marker doesn't paint over the table-name
              // labels on the left.
              <div
                className="absolute top-0 bottom-0 z-[8] pointer-events-none"
                style={{
                  left: `calc(var(--mesa-col-w) + (100% - var(--mesa-col-w)) * ${nowPct / 100})`,
                }}
              >
                {/* Hour pill stays under the sticky hour-ruler header
                    (``z-20``) so it doesn't poke through when the
                    page is scrolled vertically. */}
                <div className="absolute top-0 -translate-x-1/2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-b z-[9]">
                  {formatCurrentTime(currentTime)}
                </div>
                <div className="absolute top-0 bottom-0 w-0.5 bg-red-500 -translate-x-1/2" />
              </div>
            )}

            {/* Zone groups */}
            {groupedZones.map((zone) => {
              const zoneTables = tables
                .filter((t) => t.zoneId === zone.id)
                .toSorted((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
              if (zoneTables.length === 0) {
                return null;
              }
              const emoji = zoneEmojis[zone.name.toLowerCase()] || "";
              // Same accent resolver the map + mesa chips use, so the
              // header reads the exact hue the host sees on the floor.
              const accent = zoneAccent(zone);

              return (
                <div key={zone.id}>
                  {/* Zone header — fill + label come from ``zoneAccent``
                      (brand palette for the named zones, otherwise the
                      operator-set color), so the timeline, the map and
                      the reservation chips all read the same hue. */}
                  <div
                    className="flex items-stretch h-8 border-b border-border/60"
                    style={{ backgroundColor: accent.fill }}
                  >
                    {/* The label sits inside its own sticky wrapper so
                        it pins to the left edge during horizontal
                        scroll. The wrapper carries the zone fill
                        again so it doesn't expose the underlying
                        row when scrolled. */}
                    <div
                      className="sticky left-0 flex items-center gap-2 px-2 z-[11]"
                      style={{ backgroundColor: accent.fill }}
                    >
                      <span className="text-[11px]">{emoji}</span>
                      <span
                        className="text-[10px] font-bold tracking-wider uppercase"
                        style={{ color: accent.label }}
                      >
                        {zone.name}
                      </span>
                    </div>
                  </div>

                  {/* Table rows */}
                  {zoneTables.map((table) => {
                    const tableRes = activeReservations.filter((r: Reservation) =>
                      r.tableIds.includes(table.id),
                    );
                    const hasVip = tableHasVip(table.id);

                    return (
                      <div key={table.id} className="flex h-11 border-b border-border/30">
                        {/* Table name \u2014 sticky on the horizontal axis
                            so the host can scroll the hour ruler far
                            into the night and still see which row is
                            which. Solid background so reservation
                            bars passing underneath don't bleed
                            through. */}
                        <div className="sticky left-0 w-[var(--mesa-col-w)] shrink-0 flex items-center justify-center gap-1.5 px-1 bg-surface z-[11]">
                          <span className="text-xs font-medium truncate">{table.name}</span>
                          {hasVip && (
                            <span className="text-orange-500 text-[10px] shrink-0">{"\u2605"}</span>
                          )}
                        </div>

                        {/* Timeline row — tap on empty space inside
                            this row opens "Nueva reserva" pre-seeded
                            with the row's mesa and the clicked
                            quarter-hour. Reservation bars sit on top
                            with their own onClick and stopPropagation,
                            so clicks land on the row only when the
                            host actually tapped a hueco. */}
                        <div
                          className="flex-1 relative h-full cursor-pointer"
                          onClick={(e) => {
                            // Reservation bars are rendered as
                            // ``<button>`` children of this row. Any
                            // click that landed on (or inside) one of
                            // them should open the detail panel — not
                            // start a Nueva reserva. ``closest`` walks
                            // up the DOM from the actual click target
                            // and returns the bar element when it
                            // finds one, regardless of how the event
                            // bubbled (some browsers swallow the
                            // button's own ``stopPropagation`` when
                            // the click started as a synthetic
                            // pointer event).
                            const target = e.target as HTMLElement;
                            if (target.closest("button")) return;
                            const rect = e.currentTarget.getBoundingClientRect();
                            const pct = (e.clientX - rect.left) / rect.width;
                            const clamped = Math.max(0, Math.min(1, pct));
                            const rawHour = hourStart + clamped * totalHours;
                            // Snap down to the active 15-min slot so
                            // the host lands on a clean :00 / :15 /
                            // :30 / :45 — matching the ruler ticks.
                            // Clamp away from ``hourEnd`` so a click on
                            // the very right edge can't land on the
                            // closing-hour tick. For a shift that
                            // closes at midnight (``hourEnd = 24``) the
                            // edge tick would seed ``"12:00 AM"``,
                            // which the rest of the app reads as the
                            // START of the selected day. Cap at the
                            // last 15-min slot before close.
                            const snapped = Math.min(
                              Math.floor(rawHour / TICK_STEP_HOURS) * TICK_STEP_HOURS,
                              hourEnd - TICK_STEP_HOURS,
                            );
                            // ``openNewReservation`` only honours the
                            // seed when the surface is closed; when it
                            // is already open it parks the seed behind
                            // a discard-confirm so the operator
                            // doesn't lose what they typed. The tap is
                            // a deliberate "I want THIS mesa at THIS
                            // hour" gesture, so reset first and then
                            // open fresh with the seed. Also drop any
                            // lingering ``detailReservationId`` from a
                            // prior tap so the Sidebar doesn't surface
                            // the wrong panel on top of the new
                            // reservation flow.
                            closeNewReservation();
                            setDetailReservationId(null);
                            openNewReservation({
                              date: selectedDate,
                              time: decimalToTimeString(snapped),
                              tableIds: [table.id],
                              zone: table.zoneId,
                              // Lead the host with the smallest party
                              // that fits the mesa — bumping up is
                              // easier than catching a wrong default.
                              pax: table.minCapacity,
                            });
                            setShowSidebar(true);
                          }}
                        >
                          {tableRes.map((r: Reservation) => {
                            const startH = parseTimeHour(r.time);
                            const endH = parseTimeHour(r.endTime);
                            const leftPct = ((startH - hourStart) / totalHours) * 100;
                            const widthPct = ((endH - startH) / totalHours) * 100;
                            const style = barStyles[r.status] || barStyles.reservada;
                            const risk = isGuestRisk(r.guestName);

                            return (
                              <button
                                type="button"
                                key={r.id}
                                onClick={(e) => {
                                  // Stop the click from bubbling to the
                                  // row's tap-to-create handler — that
                                  // hueco-only flow should never fire on
                                  // top of an existing reservation.
                                  e.stopPropagation();
                                  // Drop any lingering Nueva reserva
                                  // draft from a prior tap that the
                                  // operator dismissed by clicking
                                  // outside the sheet, so the Sidebar
                                  // doesn't render that surface on top
                                  // of the detail we're about to open.
                                  closeNewReservation();
                                  setDetailReservationId(r.id);
                                  setShowSidebar(true);
                                }}
                                aria-label={`Ver reserva de ${r.guestName}`}
                                className={`absolute top-1 bottom-1 rounded-md flex items-center gap-1.5 px-2 border-l-[3px] ${style.bg} ${style.border} ${style.text} truncate z-[5] text-left cursor-pointer transition hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-primary`}
                                style={{
                                  left: `${Math.max(0, leftPct)}%`,
                                  minWidth: "60px",
                                  width: `${Math.min(widthPct, 100 - Math.max(0, leftPct))}%`,
                                }}
                              >
                                <span className="text-[11px] font-medium truncate">
                                  {r.guestName} {"\u00B7"} {r.pax}p{r.isVip ? " \u00B7 VIP" : ""}
                                </span>
                                {r.status === "sentado" && (
                                  <span className="text-[9px] font-semibold bg-emerald-500 text-white px-1.5 py-0.5 rounded-full shrink-0">
                                    Sentado
                                  </span>
                                )}
                                {/* Same rule as the map card: the guest confirmed
                                    and is not yet seated. Once seated the
                                    Sentado pill carries the information. */}
                                {r.isConfirmedByHost && r.status !== "sentado" && (
                                  <span className="text-[9px] font-semibold bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full shrink-0">
                                    Confirmado
                                  </span>
                                )}
                                {risk && (
                                  <span className="text-[9px] font-semibold bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full shrink-0">
                                    Riesgo
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer: Legend left + Stats right \u2014 stacks vertically on mobile so
          neither side gets squeezed into per-word wrapping. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 pt-4 mt-2 border-t border-border">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] text-text-secondary">
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Sentado
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> Reservado
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Riesgo
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-400" /> No Show
          </span>
        </div>
        <div className="text-[11px] text-text-muted">
          Total: {totalReservations} reservas {"\u00B7"} {totalPax} personas {"\u00B7"}{" "}
          {totalTablesUsed} mesas {"\u00B7"} {groupedZones.length} zonas
        </div>
      </div>
    </div>
  );
}
